import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { LevelData } from 'Types';
import { Completion } from './PlayContainer';
import { PreviewPhrase } from './PreviewPhrase/PreviewPhrase';
import { previewPhraseFits } from './PreviewPhrase/previewPhraseFits';
import { useElementSize } from './PreviewPhrase/useElementSize';

export type LevelPreviewSheetState = 'closed' | 'peek' | 'expanded';

interface LevelPreviewSheetProps {
  state: LevelPreviewSheetState;
  level: LevelData | undefined;
  // The sheet is as tall as its content, up to this height (at peek, up to peekMaxHeight;
  // what does not fit there is the description, which then scrolls).
  maxHeight: number;
  peekMaxHeight: number;
  // The window's height, kept current by the caller as the window resizes or rotates.
  viewportHeight: number;
  safeBottom: number;
  highestTierComplete: number;
  tierCountForLevel: number;
  onStateChange: (nextState: LevelPreviewSheetState) => void;
  onHeightChange?: (height: number) => void;
  onPlay: () => void;
}

const LevelPreviewSheet = ({
  state,
  level,
  maxHeight,
  peekMaxHeight,
  viewportHeight,
  safeBottom,
  highestTierComplete,
  tierCountForLevel,
  onStateChange,
  onHeightChange,
  onPlay,
}: LevelPreviewSheetProps) => {
  const dragStartY = useRef<number | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  // While the sheet slides away it keeps the layout it was open with.
  const lastOpenState = useRef<'peek' | 'expanded'>('peek');
  if (state !== 'closed') lastOpenState.current = state;
  const layout = lastOpenState.current;

  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || !onHeightChange || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => onHeightChange(sheet.offsetHeight));
    observer.observe(sheet);
    return () => observer.disconnect();
  }, [onHeightChange]);

  const contentBottomPadding =
    layout === 'peek'
      ? Math.max(10, Math.round(safeBottom * 0.2) + 10)
      : Math.max(10, safeBottom + 10);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    dragStartY.current = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartY.current === null) return;
    const deltaY = event.clientY - dragStartY.current;
    dragStartY.current = null;

    if (deltaY <= -40) {
      onStateChange('expanded');
      return;
    }

    if (deltaY >= 40) {
      // Swiping down collapses an expanded sheet back to peek. Closing is done by
      // tapping the dimmed roadmap behind the sheet, not by swiping down.
      if (state === 'expanded') {
        onStateChange('peek');
      }
    }
  };

  const title = level ? `Level ${level.level_number}: ${level.name}` : 'Level Preview';
  const description = level?.description || 'Select a level to preview it.';
  // Level 0 has no preview phrase on the Play bar either, so none here.
  const hasPreview = !!level && level.level_number !== 0;
  const previewSrc = hasPreview && level?.srcs ? `https://${level.srcs}` : '';
  // Too narrow for the phrase to read, the preview is left out (the peek layout then
  // drops its column). Decided from the sheet's width, which the preview does not
  // change; 0 means not measured yet.
  const sheetWidth = useElementSize(sheetRef, 0).width;
  const previewLevelNumber = level?.level_number ?? 0;
  const previewFits = (phraseWidth: number, phraseHeight: number) =>
    sheetWidth === 0 || previewPhraseFits(previewLevelNumber, phraseWidth, phraseHeight);
  const peekShowsPreview =
    hasPreview &&
    previewFits(peekPreviewPhraseWidth(sheetWidth), peekPreviewPhraseHeight(viewportHeight));
  const expandedShowsPreview =
    hasPreview && previewFits(sheetWidth - 32 - PREVIEW_CARD_INSETS, EXPANDED_PHRASE_HEIGHT);
  const sheetPreview = {
    src: previewSrc,
    levelNumber: previewLevelNumber,
    levelTitle: title,
    windowHeight: viewportHeight,
  };

  const tierSegments = (
    <Box sx={{ flexShrink: 0, marginLeft: '-12px' }}>
      <Completion highestTierComplete={highestTierComplete} tierCountForLevel={tierCountForLevel} />
    </Box>
  );

  return (
    <Box
      ref={sheetRef}
      aria-hidden={state === 'closed'}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1500,
        maxHeight: `${layout === 'peek' ? peekMaxHeight : maxHeight}px`,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingBottom: `${contentBottomPadding}px`,
        transform: state === 'closed' ? 'translateY(100%)' : 'none',
        visibility: state === 'closed' ? 'hidden' : 'visible',
        transition:
          state === 'closed'
            ? 'transform 200ms ease-out, visibility 0s linear 200ms'
            : 'transform 200ms ease-out',
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        background: 'linear-gradient(180deg, #132336 0%, #071423 100%)',
        borderTop: '1px solid rgba(173, 177, 183, 0.25)',
        boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.35)',
        overflow: 'hidden',
        pointerEvents: state === 'closed' ? 'none' : 'auto',
        color: '#CED0D4',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '10px',
          paddingBottom: '8px',
          cursor: 'ns-resize',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        <Box
          sx={{
            width: '44px',
            height: '5px',
            borderRadius: '999px',
            backgroundColor: 'rgba(206, 208, 212, 0.45)',
          }}
        />
      </Box>

      {layout === 'peek' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: peekShowsPreview
              ? 'minmax(0, 1.2fr) minmax(0, 1fr) auto'
              : 'minmax(0, 1fr) auto',
            // One row, as tall as the sheet leaves it: when the sheet is held to its peek
            // height the text column gives up height first.
            gridTemplateRows: 'minmax(0, auto)',
            columnGap: '12px',
            alignItems: 'center',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'left',
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                color: '#CA7C62',
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: '23px',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'left',
              }}
            >
              {title}
            </Box>
            <PeekDescription text={description} />
            {tierSegments}
          </Box>

          {peekShowsPreview && <SheetPreviewPhrase layout="peek" {...sheetPreview} />}

          <Box
            component="button"
            type="button"
            aria-label="Play selected roadmap level"
            onClick={onPlay}
            sx={{
              ...previewPlayButtonBaseSx,
              minWidth: `${peekPlayCircleSize}px`,
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: '13px',
                lineHeight: '14px',
                letterSpacing: '0.4px',
              }}
            >
              PLAY
            </Box>
            <Box
              component="span"
              sx={{
                width: `${peekPlayCircleSize}px`,
                height: `${peekPlayCircleSize}px`,
                borderRadius: '50%',
                backgroundColor: '#CA7C62',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PlayArrowRoundedIcon sx={{ fontSize: '43px' }} />
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              flexShrink: 0,
              color: '#CA7C62',
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '26px',
              // At 20px a long level name is wider than a 320px phone, so it
              // wraps instead of being cut; the preview gives up the height.
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {title}
          </Box>

          {tierSegments}

          {expandedShowsPreview && <SheetPreviewPhrase layout="expanded" {...sheetPreview} />}

          <Box
            sx={{
              flexShrink: 0,
              marginTop: '12px',
              fontSize: '16px',
              lineHeight: '22px',
              maxHeight: `${Math.max(90, maxHeight * 0.22)}px`,
              overflowY: 'auto',
            }}
          >
            {description}
          </Box>

          <Box
            component="button"
            type="button"
            aria-label="Play selected roadmap level"
            onClick={onPlay}
            sx={{
              ...previewPlayButtonBaseSx,
              flexShrink: 0,
              marginTop: '14px',
              width: '100%',
              height: '46px',
              borderRadius: '999px',
              backgroundColor: '#CA7C62',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              fontWeight: 700,
              fontSize: '15px',
            }}
          >
            <PlayArrowRoundedIcon sx={{ fontSize: '22px' }} />
            Play
          </Box>
        </>
      )}
    </Box>
  );
};

const peekPlayCircleSize = 72;

// The description at peek. When the sheet is held to its peek height it scrolls, and while
// more of it is below, its last line fades out, so it reads as continuing.
const PeekDescription = ({ text }: { text: string }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [moreBelow, setMoreBelow] = useState(false);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const update = () =>
      setMoreBelow(element.scrollHeight - element.clientHeight - element.scrollTop > 1);
    update();
    element.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(element);
    return () => {
      element.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [text]);
  return (
    <Box
      ref={ref}
      data-more-below={moreBelow ? 'true' : undefined}
      sx={{
        flex: '0 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        marginTop: '4px',
        fontSize: '14px',
        lineHeight: '19px',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        textAlign: 'left',
        ...(moreBelow ? fadeOutLastLine : {}),
      }}
    >
      {text}
    </Box>
  );
};

const fadeOutLastLine = {
  maskImage: 'linear-gradient(to bottom, #000 calc(100% - 19px), transparent)',
  WebkitMaskImage: 'linear-gradient(to bottom, #000 calc(100% - 19px), transparent)',
};

// The peek preview phrase is drawn no taller than the smaller of 110px and a
// quarter of the window height, so the title, description and Play read as
// the larger part of the sheet.
const peekPreviewPhraseMaxHeight = (windowHeight: number): number =>
  Math.round(Math.min(110, windowHeight * 0.25));

// The phrase sits on its light card: 4px of padding and a 1px border each side.
const PREVIEW_CARD_INSETS = 10;
// The phrase is drawn at a fixed height and re-spaces its notes to the card's width.
const peekPreviewPhraseHeight = (windowHeight: number): number =>
  peekPreviewPhraseMaxHeight(windowHeight) - PREVIEW_CARD_INSETS;
// Peek: the sheet's 16px sides and two 12px gaps, then Play (72px); the text column
// takes 1.2 parts of the rest and the preview 1.
const peekPreviewPhraseWidth = (sheetWidth: number): number =>
  (sheetWidth - 32 - 24 - peekPlayCircleSize) / 2.2 - PREVIEW_CARD_INSETS;
// Expanded: the phrase's height when the window has room; on a short window its card
// gives up height first, so Play stays on screen.
const EXPANDED_PHRASE_HEIGHT = 130;
// Squeezed below this the phrase no longer reads, so the card keeps its place but shows
// nothing (and nothing is engraved) until the window has room again.
const EXPANDED_MIN_PHRASE_HEIGHT = 60;

interface SheetPreviewPhraseProps {
  layout: 'peek' | 'expanded';
  src: string;
  levelNumber: number;
  levelTitle: string;
  windowHeight: number;
}

// The level's preview phrase in both sheet layouts: its container, size limits
// and fallback live here only.
const SheetPreviewPhrase = (props: SheetPreviewPhraseProps) =>
  props.layout === 'peek' ? <PeekPreviewPhrase {...props} /> : <ExpandedPreviewPhrase {...props} />;

const PeekPreviewPhrase = ({
  src,
  levelNumber,
  levelTitle,
  windowHeight,
}: SheetPreviewPhraseProps) => (
  <Box
    sx={{
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {src ? (
      <Box sx={previewCardSx}>
        <PreviewPhrase
          levelNumber={levelNumber}
          levelTitle={levelTitle}
          fallbackSrc={src}
          height={peekPreviewPhraseHeight(windowHeight)}
        />
      </Box>
    ) : (
      <Box sx={{ fontSize: '12px', color: '#333' }}>Preview</Box>
    )}
  </Box>
);

const ExpandedPreviewPhrase = ({ src, levelNumber, levelTitle }: SheetPreviewPhraseProps) => {
  // The card is the flex item that gives up height on a short window; the phrase is drawn
  // at the height the card is left with.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const cardHeight = useElementSize(cardRef, 150).height;
  const phraseHeight =
    cardHeight > 0
      ? Math.min(EXPANDED_PHRASE_HEIGHT, cardHeight - PREVIEW_CARD_INSETS)
      : EXPANDED_PHRASE_HEIGHT;
  const tooShort = phraseHeight < EXPANDED_MIN_PHRASE_HEIGHT;

  return src ? (
    <Box
      ref={cardRef}
      sx={{
        ...previewCardSx,
        flex: '0 1 auto',
        height: `${EXPANDED_PHRASE_HEIGHT + PREVIEW_CARD_INSETS}px`,
        minHeight: 0,
        overflow: 'hidden',
        marginTop: '12px',
        visibility: tooShort ? 'hidden' : 'visible',
      }}
    >
      {!tooShort && (
        <PreviewPhrase
          levelNumber={levelNumber}
          levelTitle={levelTitle}
          fallbackSrc={src}
          height={phraseHeight}
        />
      )}
    </Box>
  ) : (
    <Box sx={{ flexShrink: 0, marginTop: '12px', fontSize: '13px', color: '#333' }}>
      Preview unavailable
    </Box>
  );
};

const previewCardSx = {
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: '#E2E2E2',
  border: '1px solid rgba(0, 0, 0, 0.15)',
  borderRadius: '8px',
  padding: '4px',
} as const;

const previewPlayButtonBaseSx = {
  border: 0,
  padding: 0,
  backgroundColor: 'transparent',
  appearance: 'none',
  WebkitAppearance: 'none',
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
  cursor: 'pointer',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      filter: 'brightness(0.95)',
    },
  },
  '&:active': {
    filter: 'brightness(0.9)',
  },
  '&:focus-visible': {
    outline: '2px solid #6F87F1',
    outlineOffset: '2px',
  },
};

export default LevelPreviewSheet;
